import { useState } from "react";
import { api, formatApiError, formatCHF } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useT } from "../i18n/I18nContext";
import { toast } from "sonner";

function defaultDeductions(shares, base) {
  const volume = base * shares;
  if (shares === 15 && base === 2000) return [30000, 5200, 4800, 4400, 4000, 3600, 3200, 2800, 2400, 2000, 1600, 1200, 800, 400, 0];
  if (shares === 12 && base === 2000) return [24000, 4400, 4000, 3600, 3200, 2800, 2400, 2000, 1600, 1200, 800, 400];
  const step = Math.max(100, Math.round(volume / (shares * 5) / 100) * 100);
  return [volume, ...Array.from({ length: shares - 1 }, (_, i) => step * (shares - (i + 2)))];
}

export default function CreateGroupPage() {
  const t = useT();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    sharesMode: 15,
    customShares: 10,
    start_date: "2026-07-01",
    base_amount_per_share: 2000,
    bid_weekday: "sunday",
    bid_time: "10:00",
  });
  const [deductions, setDeductions] = useState(defaultDeductions(15, 2000));
  const [loading, setLoading] = useState(false);

  const shares = form.sharesMode === "custom" ? Math.max(2, Math.min(60, form.customShares || 2)) : form.sharesMode;
  const volume = shares * (form.base_amount_per_share || 0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPlanFor = (nextShares, nextBase) => {
    setDeductions(defaultDeductions(nextShares, nextBase));
  };

  const setSharesMode = (mode) => {
    const nextShares = mode === "custom" ? Math.max(2, Math.min(60, form.customShares || 2)) : mode;
    set("sharesMode", mode);
    applyPlanFor(nextShares, form.base_amount_per_share);
  };

  const setCustomShares = (n) => {
    set("customShares", n);
    const clamped = Math.max(2, Math.min(60, n || 2));
    applyPlanFor(clamped, form.base_amount_per_share);
  };

  const setBaseAmount = (v) => {
    set("base_amount_per_share", v);
    applyPlanFor(shares, v || 0);
  };

  const setDeduction = (idx, val) => {
    setDeductions((d) => d.map((x, i) => (i === idx ? val : x)));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        shares,
        start_date: form.start_date,
        base_amount_per_share: form.base_amount_per_share,
        bid_weekday: form.bid_weekday,
        bid_time: form.bid_time,
        custom_deductions: deductions.map((d) => parseInt(d || 0)),
      };
      const { data } = await api.post("/groups", payload);
      toast.success(t("admin.create.toast.created"));
      nav(`/admin/groups/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
      <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#0F4C3A]">{t("admin.create.title")}</h1>
      <p className="text-[#475569] mt-2 text-sm md:text-base">{t("admin.create.subtitle")}</p>

      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <div>
          <label className="nv-label block mb-2">{t("admin.create.name")}</label>
          <input
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="group-name-input"
            className="w-full px-4 py-3 border border-[#E2E8E4] rounded-md focus:outline-none focus:border-[#0F4C3A]"
            placeholder="z.B. Zürich 2026-A"
          />
        </div>

        <div>
          <label className="nv-label block mb-2">{t("admin.create.shares")}</label>
          <div className="grid grid-cols-3 gap-3">
            {[12, 15].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setSharesMode(n)}
                data-testid={`shares-option-${n}`}
                className={`py-3 border rounded-md font-medium transition ${
                  form.sharesMode === n ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "border-[#E2E8E4] text-[#0F4C3A] hover:bg-[#F8FAF9]"
                }`}
              >
                {t("admin.create.shares_n", { n })}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSharesMode("custom")}
              data-testid="shares-option-custom"
              className={`py-3 border rounded-md font-medium transition ${
                form.sharesMode === "custom" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "border-[#E2E8E4] text-[#0F4C3A] hover:bg-[#F8FAF9]"
              }`}
            >
              {t("admin.create.shares_custom")}
            </button>
          </div>
          {form.sharesMode === "custom" && (
            <div className="mt-3">
              <label className="nv-label block mb-2">{t("admin.create.custom_shares")}</label>
              <input
                type="number"
                min="2"
                max="60"
                value={form.customShares}
                onChange={(e) => setCustomShares(parseInt(e.target.value || "0"))}
                data-testid="custom-shares-input"
                className="w-full sm:w-48 px-4 py-3 border border-[#E2E8E4] rounded-md font-mono-nv"
              />
            </div>
          )}
        </div>

        <div>
          <label className="nv-label block mb-2">{t("admin.create.start_date")}</label>
          <input
            type="date"
            required
            value={form.start_date}
            onChange={(e) => set("start_date", e.target.value)}
            data-testid="start-date-custom"
            className="w-full sm:w-64 px-4 py-3 border border-[#E2E8E4] rounded-md"
          />
        </div>

        <div>
          <label className="nv-label block mb-2">{t("admin.create.base_amount")}</label>
          <input
            type="number"
            min="100"
            value={form.base_amount_per_share}
            onChange={(e) => setBaseAmount(parseInt(e.target.value || "0"))}
            data-testid="base-amount-input"
            className="w-full sm:w-64 px-4 py-3 border border-[#E2E8E4] rounded-md font-mono-nv"
          />
        </div>

        <div className="nv-border bg-[#F8FAF9] rounded-md p-4 sm:p-5">
          <div className="nv-label mb-3">{t("admin.create.bid_settings")}</div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="nv-label block mb-2">{t("admin.create.bid_weekday")}</label>
              <div className="flex gap-2">
                {["saturday", "sunday"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => set("bid_weekday", d)}
                    data-testid={`bid-weekday-${d}`}
                    className={`flex-1 py-2.5 border rounded-md text-sm font-medium transition ${
                      form.bid_weekday === d ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white border-[#E2E8E4] text-[#0F4C3A] hover:bg-[#F8FAF9]"
                    }`}
                  >
                    {t(d === "saturday" ? "admin.create.bid_saturday" : "admin.create.bid_sunday")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="nv-label block mb-2">{t("admin.create.bid_time")}</label>
              <input
                type="time"
                value={form.bid_time}
                onChange={(e) => set("bid_time", e.target.value)}
                data-testid="bid-time-input"
                className="w-full sm:w-36 px-4 py-2.5 border border-[#E2E8E4] rounded-md bg-white font-mono-nv"
              />
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <label className="nv-label">{t("admin.create.deduction_plan")}</label>
            <button
              type="button"
              onClick={() => applyPlanFor(shares, form.base_amount_per_share)}
              data-testid="reset-deductions-btn"
              className="text-xs px-3 py-1.5 border border-[#E2E8E4] rounded hover:bg-[#F8FAF9] text-[#475569]"
            >
              {t("admin.create.reset_plan")}
            </button>
          </div>
          <p className="text-xs text-[#94A3B8] mb-3">{t("admin.create.deduction_hint")}</p>
          <div className="nv-border bg-white rounded-md overflow-x-auto">
            <table className="w-full min-w-[420px]">
              <thead className="bg-[#F8FAF9] border-b border-[#E2E8E4]">
                <tr className="text-left">
                  <th className="nv-label p-3">{t("group.col.month")}</th>
                  <th className="nv-label p-3">{t("group.col.deduction")}</th>
                  <th className="nv-label p-3 text-right">{t("group.col.payout")}</th>
                </tr>
              </thead>
              <tbody>
                {deductions.map((d, i) => (
                  <tr key={i} className="border-b border-[#E2E8E4]">
                    <td className="p-3 font-mono-nv font-semibold">{i + 1}</td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={d}
                        onChange={(e) => setDeduction(i, parseInt(e.target.value || "0"))}
                        data-testid={`deduction-input-${i + 1}`}
                        className="w-32 px-3 py-1.5 border border-[#E2E8E4] rounded font-mono-nv text-sm"
                      />
                    </td>
                    <td className="p-3 font-mono-nv text-right text-[#2E7D32]">CHF {formatCHF(volume - (d || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-3 pt-4 flex-wrap">
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-group-button"
            className="nv-btn-primary px-6 py-3 rounded-md font-medium"
          >
            {loading ? t("admin.create.creating") : t("admin.create.submit")}
          </button>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="px-6 py-3 rounded-md border border-[#E2E8E4] text-[#0F4C3A] hover:bg-[#F8FAF9]"
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
